// ENG-067: the ready questions of a product thread's intro, per product type. The pure part: no React, no storage
// and no imports, so the contract tests and the esbuild check can pin it. The groups come from GET /n8n-config
// (`readyQuestions`, edited on /admin/ready-questions, the same for the site widget and the Genie app); this file
// picks the group of a product and carries the one fallback group for when there is no config.
//
// Matching is the same as ready_question_match in gateway/services/ready_questions.py, and both are checked against
// app/n8n/api-integration/tests/fixtures/ready_questions_match_cases.json: the product's category is the path of its
// public.gr page (`sourcepage`) after "/product/"; the longest `match` entry that is a prefix of it on a segment
// boundary wins, otherwise the default group.

export type ReadyQuestion = { id: string; el: string; en: string };

export type ReadyQuestionGroup = {
    id: string;
    name: string;
    // Category path prefixes, e.g. "books/english" (1-3 segments). Empty in the default group.
    match: string[];
    default?: boolean;
    questions: ReadyQuestion[];
};

// "seed": nothing saved on the admin page yet, the gateway serves its initial values.
export type ReadyQuestionsConfig = { groups: ReadyQuestionGroup[]; source?: "admin" | "seed" };

export const MAX_READY_QUESTIONS = 4;

// The seed's default group ("other"): the only questions the frontend carries, for when GET /n8n-config gave no
// readyQuestions (it failed, or an older gateway) or no default group. The gateway is the real source.
export const FALLBACK_READY_QUESTION_GROUP: ReadyQuestionGroup = {
    id: "other",
    name: "Σπίτι, χαρτικά, δώρα, λοιπά",
    match: [],
    default: true,
    questions: [
        { id: "features", el: "Τι χαρακτηριστικά έχει;", en: "What are its features?" },
        { id: "stock", el: "Το έχουμε στο κατάστημα;", en: "Do we have it in store?" },
        { id: "colours", el: "Υπάρχει σε άλλα χρώματα;", en: "Does it come in other colours?" },
        { id: "goes_with", el: "Τι ταιριάζει μαζί του;", en: "What goes well with it?" }
    ]
};

const PRODUCT_MARKER = "/product/";

// The lowercased path segments after "/product/" of a product page URL (query and fragment dropped), or null when
// there is no "/product/" in it.
export function productCategoryPath(sourcepage: unknown): string[] | null {
    if (typeof sourcepage !== "string") return null;
    const url = sourcepage.split(/[?#]/, 1)[0];
    const start = url.indexOf(PRODUCT_MARKER);
    if (start < 0) return null;
    return url
        .slice(start + PRODUCT_MARKER.length)
        .toLowerCase()
        .split("/")
        .filter(segment => segment !== "");
}

const isObject = (value: unknown): value is Record<string, any> => !!value && typeof value === "object" && !Array.isArray(value);

// The group and the `match` entry that chose it (null for the default group). A config without a default group
// falls back to FALLBACK_READY_QUESTION_GROUP.
export function matchReadyQuestions(config: unknown, sourcepage: unknown): { group: ReadyQuestionGroup; entry: string | null } {
    const raw = isObject(config) ? config.groups : undefined;
    const groups = (Array.isArray(raw) ? raw.filter(isObject) : []) as ReadyQuestionGroup[];
    const segments = productCategoryPath(sourcepage) ?? [];
    let best: { group: ReadyQuestionGroup; entry: string } | null = null;
    let bestLength = 0;
    for (const group of groups) {
        if (group.default === true) continue;
        for (const entry of Array.isArray(group.match) ? group.match : []) {
            if (typeof entry !== "string") continue;
            const parts = entry.split("/");
            if (parts.length > bestLength && parts.every((part, index) => part === segments[index])) {
                best = { group, entry };
                bestLength = parts.length;
            }
        }
    }
    if (best) return best;
    return { group: groups.find(group => group.default === true) ?? FALLBACK_READY_QUESTION_GROUP, entry: null };
}

// What the click sends as intro_suggestion_id (the gateway accepts ^[a-z0-9_-]{1,40}\.[a-z0-9_-]{1,40}$).
export const introSuggestionId = (groupId: string, questionId: string): string => `${groupId}.${questionId}`;

export type ReadyQuestionChoice = { id: string; text: string };

const isText = (value: unknown): value is string => typeof value === "string" && value.trim() !== "";

// The intro's questions for a product: its group's, in order, at most 4, in the UI language, each with its
// intro_suggestion_id. A group without a usable question falls back to FALLBACK_READY_QUESTION_GROUP.
export function readyQuestionsFor(config: unknown, sourcepage: unknown, language: "el" | "en"): { groupId: string; questions: ReadyQuestionChoice[] } {
    const { group } = matchReadyQuestions(config, sourcepage);
    const usable = (Array.isArray(group.questions) ? group.questions : [])
        .filter(question => isObject(question) && isText(question.id) && isText(question.el) && isText(question.en))
        .slice(0, MAX_READY_QUESTIONS);
    const chosen = usable.length && isText(group.id) ? { id: group.id, questions: usable } : FALLBACK_READY_QUESTION_GROUP;
    return {
        groupId: chosen.id,
        questions: chosen.questions.map(question => ({
            id: introSuggestionId(chosen.id, question.id),
            text: (language === "el" ? question.el : question.en).trim()
        }))
    };
}
